import { memo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { BarChart3, Pencil, RefreshCcw, Trash } from "lucide-react"

import type { ConversationPartApiResponse } from "@f2e/ui/chat-v2"
import { TipIconButton } from "@f2e/ui/components/Button"
import {
  ActionBar,
  type ChatHistory,
  type ChatMessage,
  type FeedbackBlockedReason,
} from "@f2e/ui/components/Chat"
import { Button } from "@f2e/ui/components/base"

import type { ConversationFeedback } from "@/types"

import ConversationEditDialog from "./ConversationEditDialog"
import { FeedbackSection } from "./FeedbackSection"
import { RewriteAlertDialog } from "./RewriteAlertDialog"
import { UsageMetaTooltip } from "./UsageMetaTooltip"

export const enum ActionType {
  Rewrite = "rewrite",
  Edit = "edit",
  Delete = "delete",
  Eval = "eval",
}

export interface ConversationPartFooterProps {
  history?: ChatHistory
  message: ChatMessage
  questionPart: ConversationPartApiResponse
  questionContent: string
  answerPart: ConversationPartApiResponse
  allParts: ConversationPartApiResponse[]
  hiddenActions?: ActionType[]
  rewriteTooltip?: string
  onRewritePart?: (questionPart: ConversationPartApiResponse) => void
  onEditPart?: (
    questionPart: ConversationPartApiResponse,
    content: string
  ) => void
  onDeletePart?: (questionPart: ConversationPartApiResponse) => void
  onCreateEvalPart?: (
    questionPart: ConversationPartApiResponse,
    answerPart: ConversationPartApiResponse,
    allParts: ConversationPartApiResponse[]
  ) => void
  onHelpfulFeedback?: () => Promise<boolean>
  onImproveFeedback?: (content?: string) => Promise<boolean>
  onFeedbackBlocked?: (reason: FeedbackBlockedReason) => void
  showControls?: boolean
}

const isActionHidden = (action: ActionType, hiddenActions?: ActionType[]) =>
  hiddenActions?.includes(action)

const getNumberMeta = (
  metadata: Record<string, unknown>,
  snakeKey: string,
  camelKey: string
): number | undefined => {
  const value = metadata[snakeKey] ?? metadata[camelKey]
  return typeof value === "number" ? value : undefined
}

const toConversationFeedbacks = (
  feedbacks: ConversationPartApiResponse["feedbacks"]
): ConversationFeedback[] =>
  feedbacks?.map((feedback) => ({
    id: feedback.id,
    tag: feedback.tag,
    type: feedback.type,
    identity: {
      kind: feedback.identity?.kind ?? "codeer_user",
      userId: feedback.identity?.user_id,
      userEmail: feedback.identity?.user_email,
      userName: feedback.identity?.user_name,
      externalUserId: feedback.identity?.external_user_id,
    },
    content: feedback.content ?? undefined,
    createdAt: feedback.created_at,
  })) ?? []

const ConversationPartFooter = ({
  history,
  message,
  questionPart,
  questionContent,
  answerPart,
  allParts,
  hiddenActions,
  rewriteTooltip,
  onRewritePart,
  onEditPart,
  onDeletePart,
  onCreateEvalPart,
  onHelpfulFeedback,
  onImproveFeedback,
  onFeedbackBlocked,
  showControls = true,
}: ConversationPartFooterProps) => {
  const { t, i18n } = useTranslation()
  const [showAlertDialog, setShowAlertDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const actionRef = useRef<ActionType | null>(null)
  const responseTimeMs = getNumberMeta(
    answerPart.metadata,
    "response_time_ms",
    "responseTimeMs"
  )
  const costCredits = getNumberMeta(
    answerPart.metadata,
    "cost_credits",
    "costCredits"
  )
  const hasAssociatedAgent = Boolean(
    questionPart.metadata.agent_profile ||
    questionPart.metadata.agentProfile ||
    answerPart.metadata.agent_profile ||
    answerPart.metadata.agentProfile
  )

  const handleAlertConfirm = () => {
    if (actionRef.current === ActionType.Delete) {
      onDeletePart?.(questionPart)
    } else if (actionRef.current === ActionType.Rewrite) {
      onRewritePart?.(questionPart)
    }
  }

  const extraActions = (
    <>
      {!isActionHidden(ActionType.Rewrite, hiddenActions) && onRewritePart && (
        <TipIconButton
          tip={
            rewriteTooltip || t("history.conversationFooter.actions.rewrite")
          }
          button={
            <div className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  actionRef.current = ActionType.Rewrite
                  setShowAlertDialog(true)
                }}
                className="border-0 size-8 bg-transparent"
              >
                <RefreshCcw className="h-4 w-4" />
              </Button>
            </div>
          }
        />
      )}
      {!isActionHidden(ActionType.Edit, hiddenActions) && onEditPart && (
        <TipIconButton
          tip={t("history.conversationFooter.actions.edit")}
          button={
            <div className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  actionRef.current = ActionType.Edit
                  setShowEditDialog(true)
                }}
                className="border-0 size-8 bg-transparent"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          }
        />
      )}
      {(responseTimeMs != null || costCredits != null) && (
        <UsageMetaTooltip
          responseTimeMs={responseTimeMs}
          costCredits={costCredits}
          convId={answerPart.conversation_id ?? answerPart.id ?? undefined}
          copilotField="conversationFooter.actions.usageMeta"
          align="end"
        />
      )}
      {!isActionHidden(ActionType.Eval, hiddenActions) &&
        onCreateEvalPart &&
        hasAssociatedAgent && (
          <TipIconButton
            tip={t("history.conversationFooter.actions.createEval")}
            button={
              <div className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    onCreateEvalPart(questionPart, answerPart, allParts)
                  }
                  className="border-0 size-8 bg-transparent"
                >
                  <BarChart3 className="h-4 w-4" />
                </Button>
              </div>
            }
          />
        )}
      {!isActionHidden(ActionType.Delete, hiddenActions) && onDeletePart && (
        <TipIconButton
          tip={t("history.conversationFooter.actions.delete")}
          button={
            <div className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  actionRef.current = ActionType.Delete
                  setShowAlertDialog(true)
                }}
                className="border-0 size-8 bg-transparent"
              >
                <Trash className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          }
        />
      )}
    </>
  )

  return (
    <>
      {message.createdAt && (
        <div className="mt-2 text-xs text-muted-foreground">
          {new Date(message.createdAt).toLocaleString(i18n.language)}
        </div>
      )}
      {showControls && history && (
        <ActionBar
          history={history}
          message={message}
          onHelpfulFeedback={onHelpfulFeedback}
          onImproveFeedback={onImproveFeedback}
          onFeedbackBlocked={onFeedbackBlocked}
          extraActions={extraActions}
        />
      )}
      {showControls && (
        <>
          <FeedbackSection
            feedbacks={toConversationFeedbacks(answerPart.feedbacks)}
          />
          <RewriteAlertDialog
            open={showAlertDialog}
            onOpenChange={setShowAlertDialog}
            onConfirm={handleAlertConfirm}
          />
          <ConversationEditDialog
            open={showEditDialog}
            question={questionContent}
            onOpenChange={setShowEditDialog}
            onConfirm={(content) => {
              setShowEditDialog(false)
              onEditPart?.(questionPart, content)
            }}
          />
        </>
      )}
    </>
  )
}

export default memo(ConversationPartFooter)
